import React, { useEffect, useState } from 'react';
import { Form, Radio } from 'components/ds';
import { useNavigate } from 'react-router-dom';
import { Result } from 'antd';

// ============================================================================
// COMPONENTES LOCAIS
// ============================================================================
import PageContainer from 'modules/account/components/PageContainer/PageContainer';
import { ValidatorField } from 'components/ValidatorField';

// ============================================================================
// HOOKS E SERVIÇOS
// ============================================================================
import { useGetTemplate } from 'modules/account/configs/SchemaConfig';
import { usePostPasswordOtp } from 'modules/account/services/ValidationService';
import { useGetUserByUsername } from 'modules/account/services/AccountService';

// ============================================================================
// UTILITÁRIOS E CONFIGURAÇÕES
// ============================================================================
import routes from 'modules/account/configs/RoutesConfig';
import { ApiLocalStorage } from 'utils/ApiLocalStorage';

// ============================================================================
// ESTILOS
// ============================================================================
import { ButtonLarge, DescriptionContainer, FooterDiv } from 'components/Css';
import { RadioGroupStyled, LoadingSpin } from '../styles';

// ============================================================================
// COMPONENTE
// ============================================================================

const PasswordValidation: React.FC = () => {
  const [form] = Form.useForm() as any;
  const [value, setValue] = useState('sms');
  const [contactData, setContactData] = useState<any>(null);
  const navigate = useNavigate();

  const { isLoading, data: pageConfig = {} } = useGetTemplate('passwordValidation');
  const { mutateAsync: postPasswordOtp, isLoading: isLoadingPasswordOtp } = usePostPasswordOtp();
  const { mutateAsync: getUserData, isLoading: isLoadingUserName } = useGetUserByUsername();

  const fetchUserData = async () => {
    try {
      const username = ApiLocalStorage.get('username');

      if (username) {
        setContactData(await getUserData(username));
      }
    } catch (error) {
      // Erro silencioso - tratamento pode ser adicionado aqui se necessário
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const handleNext = async (values: any) => {
    try {
      const validationType = values.validationOption;
      const contactValue = validationType === 'email' ? contactData?.email : contactData?.phoneNumber;

      await postPasswordOtp({
        participantId: contactData.participantId,
        type: validationType.toUpperCase(),
        email: validationType === 'email' ? contactValue : undefined,
        phoneNumber: validationType === 'sms' ? contactValue : undefined
      });
      
      navigate(routes.passwordValidationCode.path);
    } catch (error) {
      // Erro silencioso - tratamento pode ser adicionado aqui se necessário
    }
  };

  const onChange = (e: any) => {
    setValue(e.target.value);
  };

  const submitForm = () => {
    form.submit();
  };

  const getContactOptions = () => {
    const options = [];

    if (contactData?.email) {
      options.push({
        value: 'email',
        label: contactData.email
      });
    }

    if (contactData?.phoneNumber) {
      options.push({
        value: 'sms',
        label: ValidatorField.formatPhone(contactData.phoneNumber)
      });
    }

    return options;
  };

  useEffect(() => {
    const options = getContactOptions();

    if (options.length > 0) {
      setValue(options[0].value);
      form.setFieldValue('validationOption', options[0].value);
    }
  }, [contactData]);

  const options = getContactOptions();

  if (isLoadingPasswordOtp || isLoading || isLoadingUserName) {
    return (
      <PageContainer>
        <LoadingSpin size="large" />
      </PageContainer>
    );
  }

  return (
    <PageContainer title={pageConfig.title} navigateBack={pageConfig?.navigateBack?.()}>
      {options.length === 0 ? (
        <Result
          status="404"
          subTitle={
            <>
              Não foram encontrados dados de contato cadastrados para validação.
            </>
          }
          extra={<></>}
        />
      ) : (
        <>
          <DescriptionContainer>{pageConfig.description}</DescriptionContainer>
          <Form form={form} layout='vertical' onFinish={handleNext}>
            <Form.Item
              name='validationOption'
              rules={[{ required: true, message: 'Por favor, selecione uma opção!' }]}
            >
              <RadioGroupStyled onChange={onChange} value={value}>
                {options.map(option => (
                  <Radio key={option.value} value={option.value}>
                    {option.label}
                  </Radio>
                ))}
              </RadioGroupStyled>
            </Form.Item>
          </Form>
          <FooterDiv>
            <ButtonLarge onClick={submitForm}>
              {pageConfig.labelNext || 'Próximo'}
            </ButtonLarge>
          </FooterDiv>
        </>
      )}
    </PageContainer>
  );
};

export default PasswordValidation;
